import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import LanguageToggle from '../LanguageToggle/LanguageToggle';
import { useLanguage } from '../../context/LanguageContext';
import { scrollToSectionWithRetry } from '../../utils/scrollToSection';
import './Navbar.css';

const SCROLL_TOP_THRESHOLD = 50;

type HomeSectionId = 'services' | 'about' | 'contact';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isHome = pathname === '/';

  useEffect(() => {
    if (!isHome) {
      setIsVisible(true);
      return undefined;
    }

    const handleScroll = () => {
      setIsVisible(window.scrollY > SCROLL_TOP_THRESHOLD);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome, pathname]);

  const closeMenu = () => setIsOpen(false);

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    closeMenu();
    if (isHome) {
      e.preventDefault();
      window.history.replaceState(null, '', '/');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSectionClick = (sectionId: HomeSectionId) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    closeMenu();
    event.preventDefault();
    navigate({ pathname: '/', hash: sectionId });
    scrollToSectionWithRetry(sectionId);
  };

  const showNavbar = isVisible || isOpen;

  return (
    <nav className={`navbar ${showNavbar ? 'navbar-visible' : 'navbar-hidden'}`}>
      <div className="navbar-shell">
        <div className="navbar-container">
          <Link to="/" className="navbar-logo" onClick={handleLogoClick}>
            <img
              src={`${process.env.PUBLIC_URL}/soltec-icon.svg`}
              alt=""
              className="navbar-logo-icon"
              aria-hidden="true"
            />
            <span className="navbar-logo-text">SOLTEC</span>
          </Link>

          <div className="navbar-right">
            <div className={`navbar-links ${isOpen ? 'active' : ''}`}>
              {isHome ? (
                <>
                  <a href="#services" onClick={handleSectionClick('services')}>{t.nav.services}</a>
                  <a href="#about" onClick={handleSectionClick('about')}>{t.nav.about}</a>
                  <a href="#contact" onClick={handleSectionClick('contact')}>{t.nav.contact}</a>
                </>
              ) : (
                <>
                  <Link to={{ pathname: '/', hash: 'services' }} onClick={closeMenu}>{t.nav.services}</Link>
                  <Link to={{ pathname: '/', hash: 'about' }} onClick={closeMenu}>{t.nav.about}</Link>
                  <Link to={{ pathname: '/', hash: 'contact' }} onClick={closeMenu}>{t.nav.contact}</Link>
                </>
              )}
              <div className="mobile-controls">
                <LanguageToggle />
                <ThemeToggle />
              </div>
            </div>

            <div className="desktop-controls">
              <LanguageToggle />
              <ThemeToggle />
            </div>

            <button className="menu-icon" onClick={() => setIsOpen(!isOpen)} aria-label={t.nav.menu}>
              {isOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
